import {
  applyWfOps,
  diffWfDocument,
  ESdChangeReason,
  EWFParamType,
  invertWfOps,
  isWfStructural,
  IWfCollabOp,
  IWfDocument,
  IWFParameter,
  IWfSkippedOp,
  IWorkFlow,
  IWorkFlowDto,
  makeWfOpId,
  wfLwwKeyOf,
  wfLwwKeysConflict,
  wfRemoteOpForcesResync
} from "@kuika/kuika-cl-model";
import { AxiosResponse } from "axios";
import { isWorkflowCollabEnabled } from "../../../services/collab/collab-feature-gate";
import { CollabService } from "../../../services/collab/collab-service";
import {
  CollabRemoteChangeKind,
  CollabSessionCore,
  COLLAB_CLIENT_ID
} from "../../../services/collab/collab-session-core";
import { ICollabOpResult, ICollabParticipant, ICollabSyncResponse } from "../../../services/collab/collab-types";
import { extractWfDocument, IWfModelerLike } from "../../../services/collab/workflow-doc-extractor";
import { coalesceWfOps } from "../../../services/collab/workflow-op-coalesce";
import {
  applyWfOpsToCanvas,
  buildWfEnvelope,
  findWfSemanticNode,
  IWfCanvasSurfaces,
  IWfCollabEnvelope,
  IWfRemoteApplyResult,
  parseWfEnvelope
} from "../../../services/collab/workflow-remote-applier";
import { PartialContextProvider } from "../../../shared/patial/partial-context-provider";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { normalizeStepLaneAssignments } from "../../../shared/utility/workflow-assignment-normalization";
import { IScreenDesignContextState } from "../screen-design-context";
import { IScreenDesignContextProviderProps, ScreenDesignContextProvider } from "../screen-design-context-provider";

/** bpmn-js canvas surface used for viewbox preservation on the full-resync path. */
interface IWfCanvasViewbox {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface IWfCanvasLike {
  viewbox(box?: IWfCanvasViewbox): IWfCanvasViewbox;
}

interface IWfImportCapableModeler extends IWfModelerLike {
  importXML(xml: string): Promise<unknown>;
}

/** The workflow window stamps this after a successful importXML — the canvas-ready signal. */
interface IWfCollabStampedModeler extends IWfModelerLike {
  __wfCollabImportedWfId?: string;
}

/** Parsed side-car json of the {"xml","json"} envelope. */
interface IWfEnvelopeSideCar {
  parameters?: IWFParameter[];
  nodes?: IWorkFlow["nodes"];
  happyPathDefinition?: IWorkFlow["happyPathDefinition"];
}

export interface WorkflowCollabProviderState {
  workflowCollabSessionActive: boolean;
  workflowCollabRoster: ICollabParticipant[];
  hasOtherWorkflowParticipants: () => boolean;
  joinWorkflowCollabSession: (workflowId: string) => Promise<void>;
  leaveWorkflowCollabSession: () => void;
  canUndoWorkflowCollab: () => boolean;
  canRedoWorkflowCollab: () => boolean;
  undoWorkflowLocalOp: () => void;
  redoWorkflowLocalOp: () => void;
  onWorkflowCursorMoved: (x: number, y: number) => void;
}

/**
 * Workflow-domain face of real-time collaboration (Faz 4b) — the BPMN twin of
 * collab-provider.tsx. The entity-agnostic session machinery (CollabSessionCore) is
 * parameterized with the wf-patch engine: local gestures are captured by the save funnel
 * (workflow-provider.saveWorkFlow → commitWorkflowLocalDoc → extractor diff against the
 * core's shadow doc), remote ops apply to the shadow via `applyWfOps` and materialize onto
 * the LIVE bpmn-js canvas + selectedWf side-car via the remote applier, and any op the
 * applier cannot map confidently falls back to a FULL RESYNC from the authoritative
 * {"xml","json"} envelope (importXML + in-place selectedWf replace + viewbox preserve).
 *
 * Domain wrinkles vs the screen provider:
 *  - the join seed is the ENVELOPE, not the diffable doc (adapter.buildJoinSeedJson);
 *  - the server may REJECT the seed (envelope parse failure) → seedRejected → this tab
 *    degrades to legacy single-user editing with one info toast, no session;
 *  - authoritative snapshots arrive as envelopes too, and importXML is async — the
 *    adapter parses a placeholder synchronously, the provider imports the envelope, then
 *    re-seeds the core's shadow from a fresh live extract (reseedShadowFromLive). Local
 *    capture is suppressed for the (small) import window;
 *  - the ops ack may carry server-origin `syntheticOps` (wfLaneDerived) — the core applies
 *    them through the same remote path;
 *  - wf-specific convergence rules (proven by the wf convergence sim, T1): a remote
 *    whole-record upsert conflicting with any own write on the same element or a remote
 *    structural op while own structural ops are unacked → full resync
 *    (`wfRemoteOpForcesResync` — wf ships no resolvedIndex corrections); LWW keys compare
 *    prefix-aware (`wfLwwKeysConflict` — "/a" races "/a/b"); an accepted wfElementRemove
 *    ack carries the SERVER-stamped `cascadedIds`, adopted via `makeAckAdoptionOps` (the
 *    hub suppresses the origin's echo — the ack is the only channel for the stamp).
 *
 * CROSS-PARTITION METHODS MUST GO THROUGH this.partial — this.state holds the partition
 * defaultState STUBS (documented data-loss pitfall).
 */
export class WorkflowCollabProvider extends PartialContextProvider<
  ScreenDesignContextProvider,
  IScreenDesignContextProviderProps,
  IScreenDesignContextState,
  WorkflowCollabProviderState
> {
  static defaultState: WorkflowCollabProviderState = {
    workflowCollabSessionActive: false,
    workflowCollabRoster: [],
    hasOtherWorkflowParticipants: () => false,
    joinWorkflowCollabSession: async () => {},
    leaveWorkflowCollabSession: () => {},
    canUndoWorkflowCollab: () => false,
    canRedoWorkflowCollab: () => false,
    undoWorkflowLocalOp: () => {},
    redoWorkflowLocalOp: () => {},
    onWorkflowCursorMoved: () => {}
  };

  /** True while a remote batch / snapshot is being materialized — bpmn events are not local. */
  private applyingRemoteWorkflowChange: boolean = false;

  /** True between an authoritative-snapshot commit and the shadow reseed (async importXML). */
  private workflowResyncInFlight: boolean = false;

  /** Envelope stashed by parseSnapshot for the resync commit that follows it. */
  private pendingResyncEnvelope: IWfCollabEnvelope | null = null;

  /** The doc handed out by getLiveDoc — applyOps on THIS reference also materializes to canvas. */
  private liveDocMirror: IWfDocument | null = null;

  private core: CollabSessionCore<IWfDocument, IWfCollabOp> = new CollabSessionCore<IWfDocument, IWfCollabOp>({
    entityRoute: "workflow",
    adapter: {
      getSerializableDoc: (): IWfDocument | null => this.extractLiveDoc(),
      getLiveDoc: (): IWfDocument => {
        const doc: IWfDocument | null = this.extractLiveDoc();
        const live: IWfDocument = doc ?? this.emptyDoc();
        this.liveDocMirror = live;
        return live;
      },
      getDocId: (doc: IWfDocument): string | null => (doc?.id ? String(doc.id) : null),
      diff: (before: IWfDocument, after: IWfDocument): IWfCollabOp[] => diffWfDocument(before, after, makeWfOpId),
      invert: (before: IWfDocument, ops: IWfCollabOp[]): IWfCollabOp[] => invertWfOps(before, ops, makeWfOpId),
      applyOps: (doc: IWfDocument, ops: IWfCollabOp[]): IWfCollabOp[] => {
        const skipped: IWfSkippedOp[] = applyWfOps(doc, ops).skippedOps;
        if (doc === this.liveDocMirror) {
          this.materializeRemoteOps(ops);
        }
        const skippedIds: Set<string> = new Set<string>(skipped.map((entry: IWfSkippedOp) => entry.opId));
        return ops.filter((op: IWfCollabOp) => skippedIds.has(op.opId));
      },
      commitRemoteDoc: (_doc: IWfDocument, kind: CollabRemoteChangeKind): void => {
        if (kind === "resync") {
          const envelope: IWfCollabEnvelope | null = this.pendingResyncEnvelope;
          this.pendingResyncEnvelope = null;
          if (envelope) {
            void this.applyResyncEnvelope(envelope);
          } else {
            this.workflowResyncInFlight = false;
          }
          return;
        }
        // Granular op batch: the canvas/side-car were already materialized in applyOps —
        // push a state refresh so panels re-render off the mutated selectedWf.
        this.setState({
          selectedWf: this.partial.workflowProvider.state.selectedWf,
          changeReason: ESdChangeReason.RemoteWorkflowCollabOp
        });
        this.guardSelectedNodeAfterRemoteApply();
      },
      parseSnapshot: (json: string): IWfDocument | null => {
        const envelope: IWfCollabEnvelope | null = parseWfEnvelope(json);
        if (!envelope) return null;
        // The real doc only exists after the async importXML — hand the core a placeholder
        // shadow and suppress local capture until reseedShadowFromLive() lands.
        this.pendingResyncEnvelope = envelope;
        this.workflowResyncInFlight = true;
        const workflowId: string =
          this.core.getSessionEntityId() ?? this.partial.workflowProvider.state.selectedWf?.id ?? "";
        return { id: workflowId, elements: {}, elementOrder: {} };
      },
      lwwKeyOf: (op: IWfCollabOp): string | null => wfLwwKeyOf(op),
      isStructural: (op: IWfCollabOp): boolean => isWfStructural(op),
      // Prefix-aware LWW: ancestor/descendant prop paths ("/a" vs "/a/b") suppress each
      // other — the exact-key rule alone let an older nested set re-create a removed
      // subtree as a husk (wf convergence sim, T1 finding 3).
      lwwKeysConflict: (a: string, b: string): boolean => wfLwwKeysConflict(a, b),
      // Conservative wf conflict rules (T1 findings 1+2): a whole-record upsert racing any
      // write on the same element, or a remote structural op while our own structural ops
      // are unacked, recovers by resync — wf has no resolvedIndex-style corrections.
      remoteOpForcesResync: (
        remoteOp: IWfCollabOp,
        remoteSeq: number,
        pendingOps: readonly IWfCollabOp[],
        ackedKeySeq: ReadonlyMap<string, number>
      ): boolean => wfRemoteOpForcesResync(remoteOp, remoteSeq, pendingOps, ackedKeySeq),
      coalesce: (ops: IWfCollabOp[]): IWfCollabOp[] => coalesceWfOps(ops),
      remintForReplay: (op: IWfCollabOp): IWfCollabOp => ({ ...op, opId: makeWfOpId() }),
      makeResolvedIndexCorrection: (): IWfCollabOp | null => null, // wf ops carry no resolvedIndex
      skippedOpForcesResync: (op: IWfCollabOp): boolean => isWfStructural(op),
      // Origin cascade adoption (T1 finding 4): the hub suppresses our own broadcast echo,
      // so the SERVER-stamped remove cascade reaches this origin only through the ack —
      // re-apply the stamped remove so shadow + live + canvas drop the authority's full
      // closure (idempotent: already-removed targets are accepted no-ops).
      makeAckAdoptionOps: (op: IWfCollabOp, result: ICollabOpResult): IWfCollabOp[] => {
        if (op.kind !== "wfElementRemove") return [];
        const cascadedIds: string[] | undefined = result.cascadedIds;
        if (!cascadedIds || cascadedIds.length === 0) return [];
        // KOA-18191 (fast-undo race): if the user undid the delete BEFORE this ack landed,
        // the removed elements are alive again locally and the restoring upserts are
        // already queued to the server — re-applying the stamped remove would wipe the
        // restored task/flows from the canvas while the server keeps them. Adopt the
        // cascade only for ids that are STILL gone locally.
        const live: IWfDocument | null = this.extractLiveDoc();
        const isGone = (id: string): boolean => !live || live.elements[id] === undefined;
        if (op.elementId && !isGone(op.elementId)) return [];
        const stillGone: string[] = cascadedIds.filter(isGone);
        if (stillGone.length === 0) return [];
        return [{ ...op, cascadedIds: stillGone }];
      },
      remapIds: (): void => {}, // no server-side id remap by contract
      buildJoinSeedJson: async (): Promise<string | null> => this.buildJoinEnvelope()
    },
    host: {
      onSessionJoined: (roster: ICollabParticipant[]): void => {
        this.setState({ workflowCollabSessionActive: true, workflowCollabRoster: roster });
      },
      onSessionLeft: (): void => {
        if (this.state.workflowCollabSessionActive) {
          this.setState({ workflowCollabSessionActive: false, workflowCollabRoster: [] });
        }
      },
      applyRosterUpdate: (roster: ICollabParticipant[]): void => {
        this.setState({ workflowCollabRoster: roster });
      },
      getRoster: (): ICollabParticipant[] => this.state.workflowCollabRoster,
      getSelectedIds: (): string[] => {
        const selectedNodeId: string | undefined = this.state.selectedWfNode?.id;
        return selectedNodeId ? [selectedNodeId] : [];
      },
      onSeedRejected: (reason: string): void => {
        KMainFunctions.displayWarningNotification(
          "Live collaboration is unavailable for this process — continuing in single-user mode.",
          { duration: 6 }
        );
      }
    }
  });

  getProviderValues = (): WorkflowCollabProviderState => {
    return {
      workflowCollabSessionActive: this.state.workflowCollabSessionActive,
      workflowCollabRoster: this.state.workflowCollabRoster,
      hasOtherWorkflowParticipants: this.hasOtherWorkflowParticipants,
      joinWorkflowCollabSession: this.joinWorkflowCollabSession,
      leaveWorkflowCollabSession: this.leaveWorkflowCollabSession,
      canUndoWorkflowCollab: this.canUndoWorkflowCollab,
      canRedoWorkflowCollab: this.canRedoWorkflowCollab,
      undoWorkflowLocalOp: this.undoWorkflowLocalOp,
      redoWorkflowLocalOp: this.redoWorkflowLocalOp,
      onWorkflowCursorMoved: this.onWorkflowCursorMoved
    };
  };

  // ---- lifecycle ------------------------------------------------------------------------

  public joinWorkflowCollabSession = async (workflowId: string): Promise<void> => {
    if (!isWorkflowCollabEnabled()) return;
    if (!workflowId || workflowId === "0") return;
    // Stale-hash ghost: a legacy wfhash from a pre-collab session must never suppress the
    // first save after the flag flips back off.
    sessionStorage.removeItem(`wfhash_${workflowId}`);
    await this.core.join(workflowId);
  };

  public leaveWorkflowCollabSession = (): void => {
    const workflowId: string | null = this.core.getSessionEntityId();
    this.core.leave();
    this.workflowResyncInFlight = false;
    this.pendingResyncEnvelope = null;
    if (workflowId) {
      sessionStorage.removeItem(`wfhash_${workflowId}`);
    }
  };

  public isWorkflowSessionActive = (): boolean => {
    return this.core.isSessionActive();
  };

  /**
   * Someone ELSE is editing this process right now. The BPMN twin of collab-provider's
   * `hasOtherParticipants`, and the same distinction it draws: a live session is not the
   * same thing as company. Undo/redo is the one place that difference decides which history
   * is authoritative — alone, the modeler's own command stack is; with others on the
   * canvas, rewinding it would revert THEIR remotely applied commands, so the per-user
   * inverse-op stack takes over instead.
   */
  public hasOtherWorkflowParticipants = (): boolean => {
    return (
      this.core.isSessionActive() &&
      this.state.workflowCollabRoster.some((p: ICollabParticipant): boolean => p.clientId !== COLLAB_CLIENT_ID)
    );
  };

  /** True while this provider is materializing remote/authoritative changes onto the canvas. */
  public isApplyingRemoteWorkflowChange = (): boolean => {
    return this.applyingRemoteWorkflowChange || this.core.isApplyingRemoteChange();
  };

  // ---- local mutations → ops (the save funnel calls this) ---------------------------------

  /** Extract → diff against the shadow → queue ops (throttled flush). The whole local path. */
  public commitWorkflowLocalDoc = (): void => {
    if (!this.core.isSessionActive()) return;
    if (this.isApplyingRemoteWorkflowChange() || this.workflowResyncInFlight) return;
    this.core.emitLocalOps();
  };

  public flushWorkflowOps = async (): Promise<void> => {
    await this.core.flushOps();
  };

  /**
   * Explicit save (Ctrl+S) in collab mode: the document already flows through ops — flush
   * whatever is queued and push the save metadata (name); the server persists from its
   * authoritative state on its flush cadence. Mirrors collab-provider.saveThroughCollab.
   */
  public saveWorkflowThroughCollab = async (name: string): Promise<boolean> => {
    const workflowId: string | null = this.core.getSessionEntityId();
    if (!workflowId) return false;
    try {
      await this.core.flushOps();
      const res: AxiosResponse<ICollabSyncResponse> = await CollabService.Sync(
        workflowId,
        COLLAB_CLIENT_ID,
        undefined,
        name,
        workflowId,
        "workflow"
      );
      return !!res && res.status === 200;
    } catch (error: unknown) {
      console.error("[collab] workflow save failed:", error);
      return false;
    }
  };

  // ---- per-user undo/redo entry points (session-scoped, via the core) ----------------------

  public canUndoWorkflowCollab = (): boolean => this.core.canUndo();

  public canRedoWorkflowCollab = (): boolean => this.core.canRedo();

  public undoWorkflowLocalOp = (): void => {
    this.core.undo();
  };

  public redoWorkflowLocalOp = (): void => {
    this.core.redo();
  };

  // ---- presence -----------------------------------------------------------------------------

  /** Selection changed on the BPMN canvas — throttle-broadcast it as presence. */
  public broadcastWorkflowSelection = (): void => {
    if (!this.core.isSessionActive()) return;
    this.core.scheduleSelectionBroadcast();
  };

  /** Local pointer in DIAGRAM coordinates (overlay module throttles the capture). */
  public onWorkflowCursorMoved = (x: number, y: number): void => {
    if (!this.core.isSessionActive()) return;
    this.core.onLocalCursorMoved(x, y);
  };

  /**
   * Right-panel safety after a remote batch: the open property editor holds an object
   * REFERENCE into the mutated side-car. Same id still present → re-resolve so the panel
   * re-binds fresh values; deleted remotely → clear the selection with an honest toast
   * (editing a ghost node would silently discard every subsequent field change).
   */
  private guardSelectedNodeAfterRemoteApply = (): void => {
    const selectedNodeId: string | undefined = this.partial.workflowProvider.state.selectedWfNode?.id;
    if (!selectedNodeId) return;
    const selectedWf: IWorkFlow | undefined = this.partial.workflowProvider.state.selectedWf;
    const stillExists: boolean = findWfSemanticNode(selectedWf, selectedNodeId) !== undefined;
    if (stillExists) {
      this.partial.workflowProvider.setSelectedWfNodeId(selectedNodeId);
      return;
    }
    this.partial.workflowProvider.clearWfNodeSelection();
    KMainFunctions.displayWarningNotification("The element you were editing was deleted by a collaborator.", {
      duration: 5
    });
  };

  // ---- internals ------------------------------------------------------------------------------

  private extractLiveDoc = (): IWfDocument | null => {
    const selectedWf: IWorkFlow | undefined = this.partial.workflowProvider.state.selectedWf;
    const modeler: IWfCollabStampedModeler | undefined = this.partial.workflowProvider.getModeler() as
      | IWfCollabStampedModeler
      | undefined;
    if (!modeler || !selectedWf) return null;
    // Canvas readiness: the workflow window stamps the modeler AFTER importXML succeeds.
    // Until the stamp matches, the registry still shows the previous diagram — extracting
    // it would seed/diff a foreign document.
    if (modeler.__wfCollabImportedWfId !== selectedWf.id) return null;
    return extractWfDocument(modeler, selectedWf);
  };

  private emptyDoc = (): IWfDocument => {
    const workflowId: string =
      this.core.getSessionEntityId() ?? this.partial.workflowProvider.state.selectedWf?.id ?? "";
    return { id: workflowId, elements: {}, elementOrder: {} };
  };

  private buildJoinEnvelope = async (): Promise<string | null> => {
    const workflowProvider = this.partial.workflowProvider;
    const selectedWf: IWorkFlow | undefined = workflowProvider.state.selectedWf;
    if (!selectedWf) return null;
    try {
      // Prefer the live canvas export (exactly what the extractor's shadow will describe);
      // fall back to the just-loaded persisted xml when the export is unavailable.
      const xml: string | undefined = (await workflowProvider.getXmlFromModeler()) as string | undefined;
      if (xml) {
        selectedWf.xml = xml;
      }
    } catch {
      // keep selectedWf.xml as loaded
    }
    const dto: IWorkFlowDto | undefined = workflowProvider.convertWFToWfDto(selectedWf);
    if (!dto || !dto.xml || typeof dto.json !== "string") return null;
    return buildWfEnvelope(dto.xml, dto.json);
  };

  /** Granular remote materialization — canvas + side-car, inside the isApplyingRemote guard. */
  private materializeRemoteOps = (ops: IWfCollabOp[]): void => {
    const workflowProvider = this.partial.workflowProvider;
    const selectedWf: IWorkFlow | undefined = workflowProvider.state.selectedWf;
    const modeler: IWfModelerLike | undefined = workflowProvider.getModeler() as IWfModelerLike | undefined;
    if (!selectedWf || !modeler) return;
    let surfaces: IWfCanvasSurfaces;
    try {
      surfaces = {
        modeling: modeler.get("modeling") as IWfCanvasSurfaces["modeling"],
        elementFactory: modeler.get("elementFactory") as IWfCanvasSurfaces["elementFactory"],
        bpmnFactory: modeler.get("bpmnFactory") as IWfCanvasSurfaces["bpmnFactory"],
        elementRegistry: modeler.get("elementRegistry") as IWfCanvasSurfaces["elementRegistry"],
        syncService: workflowProvider.getSyncService()
      };
    } catch {
      return; // read-only viewer or torn-down modeler — the doc stays authoritative
    }
    this.applyingRemoteWorkflowChange = true;
    try {
      const result: IWfRemoteApplyResult = applyWfOpsToCanvas(surfaces, selectedWf, ops);
      if (result.needsResync) {
        this.core.requestResyncFromAuthority();
      }
    } finally {
      this.applyingRemoteWorkflowChange = false;
    }
  };

  /**
   * FULL RESYNC fallback: authoritative envelope → importXML (viewbox + selection
   * preserved) + in-place selectedWf replace (same object reference on purpose — the
   * workflow window re-imports on xml IDENTITY change, and we already imported). Ends by
   * re-seeding the core's shadow from a fresh live extract.
   */
  private applyResyncEnvelope = async (envelope: IWfCollabEnvelope): Promise<void> => {
    const workflowProvider = this.partial.workflowProvider;
    const selectedWf: IWorkFlow | undefined = workflowProvider.state.selectedWf;
    const modeler: IWfImportCapableModeler | undefined = workflowProvider.getModeler() as
      | IWfImportCapableModeler
      | undefined;
    if (!selectedWf || !modeler || typeof modeler.importXML !== "function") {
      this.workflowResyncInFlight = false;
      return;
    }
    this.applyingRemoteWorkflowChange = true;
    try {
      let canvas: IWfCanvasLike | undefined;
      let viewbox: IWfCanvasViewbox | undefined;
      try {
        canvas = modeler.get("canvas") as IWfCanvasLike | undefined;
        viewbox = canvas?.viewbox();
      } catch {
        canvas = undefined;
      }

      await modeler.importXML(envelope.xml);

      if (canvas && viewbox) {
        try {
          canvas.viewbox({ x: viewbox.x, y: viewbox.y, width: viewbox.width, height: viewbox.height });
        } catch {
          // viewbox restore is cosmetic — never fail the resync for it
        }
      }

      // In-place side-car replace, mirroring convertWFDtoToWf's normalizations.
      selectedWf.xml = envelope.xml;
      try {
        const sideCar: IWfEnvelopeSideCar = JSON.parse(envelope.json) as IWfEnvelopeSideCar;
        selectedWf.nodes = sideCar.nodes;
        selectedWf.happyPathDefinition = sideCar.happyPathDefinition;
        normalizeStepLaneAssignments(selectedWf.nodes);
        selectedWf.parameters = sideCar.parameters?.map((p: IWFParameter): IWFParameter => ({
          ...p,
          isInput: p.parameterType === EWFParamType.Input,
          isBusinessObject: p.isBusinessObject === true
        }));
      } catch (error: unknown) {
        console.error("[collab] refreshing workflow parameters after a resync failed:", error);
      }

      this.setState({ selectedWf, changeReason: ESdChangeReason.RemoteWorkflowResync });
    } catch (error: unknown) {
      console.error("[collab] workflow resync failed:", error);
    } finally {
      this.applyingRemoteWorkflowChange = false;
      this.workflowResyncInFlight = false;
      // Shadow == extractor(live) again — local diffs resume from the authoritative state.
      this.core.reseedShadowFromLive();
    }
  };
}
